// New job (ui-spec §3.5, §5.1 "+ New job"): the create flow over the Job API shape, so the YAML, CLI and Python
// tabs show exactly what Launch sends. `?from=job/<name>` (Run again) starts from that Job's spec; `?gpu=&count=`
// (Run on this GPU) sets the GPU, also over a restored draft.
import { useKList } from "@nodus/api";
import {
  Advanced,
  Button,
  Callout,
  cliCommand,
  EstimateBlock,
  Field,
  generatedName,
  Input,
  ListSkeleton,
  ManifestTabs,
  nameProblem,
  Page,
  PageHeader,
  Select,
  UncertainOutcome,
  usePrefs,
  type Manifest,
  type OfferingLike,
} from "@nodus/ui";
import { useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { parse as shellTokens } from "shell-quote";
import { join as joinCommand, split as splitCommand } from "shlex";
import { ApiErrorCallout } from "../../app/api-error";
import { useCreateFlow, useDuplicateSource } from "../../app/create-flow";
import { CreationSteps, ManifestReview } from "../../app/creation-steps";
import { ComputeChooser } from "../console/compute-chooser";
import { useAuth } from "../../auth/auth-context";
import type { JobSpec, Scope } from "./model";
import { applyComputeSelection, computeFromSpec, type ComputeSelection } from "../console/compute-selection";
import { usePools } from "../infrastructure/hooks";

type JobManifest = Manifest & { metadata: { name: string }; spec: JobSpec };

/** Moves the top-level `gpu` shorthand some older manifests carry into spec.resources.gpu. */
export function normalizeJob(m: Manifest): Manifest {
  const spec = { ...((m.spec ?? {}) as JobSpec) };
  if (spec.gpu?.family && !spec.resources?.gpu) spec.resources = { ...spec.resources, gpu: { type: [spec.gpu.family], count: spec.gpu.count ?? 1 } };
  delete spec.gpu;
  delete spec.state;
  return { ...m, spec };
}

export function JobNew({ scope, from, compute, pool }: { scope: Scope; from?: string; compute?: ComputeSelection; pool?: string }) {
  const source = useDuplicateSource(from, scope, "Job");
  if (source.error) return <Page><PageHeader title="Could not load the original job" /><ApiErrorCallout error={source.error} scope={scope} onRetry={source.retry} /></Page>;
  if (source.loading) {
    return (
      <Page>
        <ListSkeleton />
      </Page>
    );
  }
  return <JobForm key={JSON.stringify([from, pool, compute])} scope={scope} {...(from ? { draftSource: from } : pool ? { draftSource: `pool/${pool}` } : {})} {...(compute ? { compute } : {})} {...(pool ? { pool } : {})} {...(source.manifest ? { initial: normalizeJob(source.manifest) } : {})} />;
}

function JobForm({ scope, initial: from, compute, pool: poolPrefill, draftSource }: { scope: Scope; initial?: Manifest; compute?: ComputeSelection; pool?: string; draftSource?: string }) {
  const { session } = useAuth();
  const prefs = usePrefs();
  const navigate = useNavigate();
  const offerings = useKList("Offering", { org: scope.org }, { watch: false });
  const pools = usePools(scope);
  const initial = React.useMemo<Manifest>(
    () =>
      from ?? {
        apiVersion: "nodus.dev/v1",
        kind: "Job",
        metadata: { name: generatedName("job"), namespace: scope.project },
        spec: { image: "nodus/pytorch", command: ["python", "-c", "import torch; print('PyTorch:', torch.__version__); print('CUDA available:', torch.cuda.is_available())"], resources: poolPrefill && !compute?.gpu ? { cpu: "2", memory: "4Gi" } : { gpu: { minMemory: "24Gi", count: 1 } }, ...(poolPrefill ? { placement: { pool: poolPrefill } } : {}) },
      },
    [from, scope.project, poolPrefill, compute?.gpu],
  );
  const selectedInitial = React.useMemo(() => applyComputeSelection(initial, compute), [initial, compute]);
  const flow = useCreateFlow({ kind: "Job", scope, userId: session?.user.id ?? "", initial: selectedInitial, draftSource: compute?.gpu || compute?.placement ? `compute/${JSON.stringify(compute)}/${draftSource ?? "new"}` : draftSource, supply: offerings.data?.items as OfferingLike[] | undefined });
  const m = flow.manifest as JobManifest;
  const commandKey = JSON.stringify(m.spec.command ?? []);
  const [commandEdit, setCommandEdit] = React.useState<{ key: string; text: string; error?: string }>();
  const editedCommand = commandEdit?.key === commandKey ? commandEdit : undefined;
  const gpu = m.spec.resources?.gpu;
  const selectedPool = (pools.data?.items ?? []).find((p) => p.metadata.name === m.spec.placement?.pool);
  const update = (patch: (x: JobManifest) => void) => {
    const next = structuredClone(m);
    patch(next);
    flow.setManifest(next);
  };
  const changeCommand = (text: string) => {
    try {
      // shlex keeps variable references literal; shell-quote only identifies syntax requiring an explicit shell.
      const argv = splitCommand(text);
      if (shellTokens(text).some((token) => typeof token !== "string")) {
        throw new Error("Quote literal operators or use bash -lc 'your shell command' for pipes, redirects and expansion.");
      }
      setCommandEdit({ key: JSON.stringify(argv), text });
      update((x) => void (x.spec.command = argv));
    } catch (error) {
      setCommandEdit({ key: commandKey, text, error: error instanceof Error ? error.message : "Check the command's quotes and escapes." });
    }
  };
  React.useEffect(() => {
    flow.setManifest(applyComputeSelection(flow.manifest, compute));
    // Applied once on open; later edits in the form win.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const changeCompute = (next: ComputeSelection) => flow.setManifest(applyComputeSelection(flow.manifest, next));
  const problem = nameProblem(m.metadata.name);
  const done = flow.launch.state === "done" ? flow.launch.object : undefined;
  React.useEffect(() => {
    if (done) void navigate({ to: "/$org/$project/jobs/$name/$tab", params: { ...scope, name: done.metadata.name, tab: "logs" } });
  }, [done, navigate, scope]);

  const runArgs = ["run", ...(gpu?.type?.[0] ? ["--gpu", `${gpu.type[0]}${(gpu.count ?? 1) > 1 ? `:${gpu.count}` : ""}`] : []), "--image", m.spec.image ?? "", "--name", m.metadata.name];
  return (
    <Page>
      <PageHeader title="New job" parents={[{ label: "Runs", href: `/${encodeURIComponent(scope.org)}/${encodeURIComponent(scope.project)}/runs` }]} />
      {flow.draftRestored ? (
        <Callout tone="neutral" className="mb-6" action={<Button size="sm" variant="ghost" onClick={flow.discardDraft}>Discard</Button>}>
          Restored your draft.
        </Callout>
      ) : null}
      <CreationSteps invalid={Boolean(problem) || !m.spec.image || Boolean(editedCommand?.error)} busy={flow.launch.state === "launching" || Boolean(done)}
        configure={<ManifestTabs
                manifest={flow.manifest}
                onManifestChange={flow.setManifest}
                scope={scope}
                omitScope={prefs.omitScope}
                {...(!m.spec.placement?.community && !m.spec.placement?.pool && !m.spec.placement?.offerings?.length && !m.spec.placement?.regions?.length && !gpu?.minMemory ? { shortCommand: cliCommand(runArgs, scope, { omitScope: prefs.omitScope, trailing: m.spec.command ?? [] }) } : {})}
                form={
                  <div className="grid gap-4">
                    <Field id="job-image" label="Container image" required>
                      <Input value={m.spec.image ?? ""} className="font-mono" onChange={(e) => update((x) => void (x.spec.image = e.target.value))} />
                    </Field>
                    <Field id="job-command" label="Command" error={editedCommand?.error} hint="Quotes group arguments. Use bash -lc '…' when you need a shell.">
                      <Input value={editedCommand?.text ?? joinCommand(m.spec.command ?? [])} className="font-mono" onChange={(e) => changeCommand(e.target.value)} />
                    </Field>
                    <Advanced label="Name and spending limit" defaultOpen={Boolean(problem)}>
                    <Field id="job-name" label="Name" error={problem} required>
                      <Input value={m.metadata.name} className="font-mono" onChange={(e) => update((x) => void (x.metadata.name = e.target.value))} />
                    </Field>

                      <Field id="job-max-cost" label="Cost limit (USD)">
                        <Input inputMode="decimal" value={m.spec.maxCostUSD ?? ""} onChange={(e) => update((x) => void (e.target.value ? (x.spec.maxCostUSD = e.target.value) : delete x.spec.maxCostUSD))} />
                      </Field>
                    </Advanced>
                  </div>
                }
              />}
        resources={<div className="space-y-4">{(pools.data?.items.length || m.spec.placement?.pool) ? <div className="max-w-3xl">                    <Field id="job-pool" label="Where to run" hint={m.spec.placement?.pool ? selectedPool?.spec?.routing?.mode === "Only" ? "Runs only on your own hosts. Work waits when the pool is full." : `Uses your private pool first. Market fallback: ${selectedPool?.spec?.routing?.burstToMarket ?? "see pool routing"}. Your cloud charges continue.` : "Uses your cloud accounts first when enabled, within their spending limits. Your BYOC settings control whether work can fall back to Nodus."}>
                      <Select id="job-pool" value={m.spec.placement?.pool ?? "__market"} onValueChange={(v) => update((x) => {
                        const placement = { ...x.spec.placement };
                        if (v === "__market") delete placement.pool;
                        else { placement.pool = v; delete placement.offerings; delete placement.community; }
                        x.spec.placement = placement;
                      })} options={[{ value: "__market", label: "Nodus capacity" }, ...(pools.data?.items ?? []).map((p) => ({ value: p.metadata.name, label: `Private pool · ${p.metadata.name}` }))]} />
                    </Field>
</div> : null}<ComputeChooser pool={m.spec.placement?.pool} items={(offerings.data?.items ?? []) as OfferingLike[]} value={computeFromSpec(m.spec)} onChange={changeCompute} loading={offerings.isLoading} error={offerings.error} onRetry={() => void offerings.refetch()} /></div>}
        review={<><ManifestReview manifest={flow.manifest} /><EstimateBlock
                {...(flow.estimate.view ? { estimate: flow.estimate.view } : {})}
                loading={flow.estimate.loading}
                stale={flow.estimate.stale}
                {...(flow.estimate.error ? { error: flow.estimate.error } : {})}
              />
              {flow.launch.state === "repriced" ? <Callout tone="warning" role="alert" title="Prices changed. Review the new estimate, then launch again." /> : null}
              {flow.launch.state === "uncertain" ? (
                <UncertainOutcome idempotencyKey={flow.idempotencyKey} onCheck={() => void flow.checkStatus()} onRetry={() => void flow.retry()} {...(flow.launch.requestId ? { requestId: flow.launch.requestId } : {})} />
              ) : null}
              {flow.launch.state === "error" ? <ApiErrorCallout error={flow.launch.error} scope={scope} /> : null}</>}
        launch={<Button disabled={Boolean(problem) || Boolean(editedCommand?.error) || !m.spec.image || !flow.canSubmit} onClick={() => void flow.submit()}>
                Launch job
              </Button>}
      />
    </Page>
  );
}
